import { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCategories, GroupWithCategories } from '@/hooks/useCategories';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  Loader2,
  Tag,
  AlertCircle,
  Settings,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import type { Database } from '@/lib/database.types';
import type { TranslationKey } from '@/lib/i18n';

type Category = Database['public']['Tables']['categories']['Row'];
type CategoryGroup = Database['public']['Tables']['category_groups']['Row'];
type TranslateFn = (key: TranslationKey) => string;

// Available colors for selection
const AVAILABLE_COLORS = [
  '#16c47f',
  '#3b82f6',
  '#8b5cf6',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#10b981',
  '#6366f1',
  '#f97316',
  '#14b8a6',
  '#06b6d4',
  '#6b7280',
  '#a855f7',
  '#84cc16',
  '#22c55e',
  '#0ea5e9',
  '#f43f5e',
  '#78716c',
];

// Sortable Category Item Component
interface SortableCategoryItemProps {
  category: Category;
  getCategoryIcon: (icon: string | null) => string;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onToggleHidden: (category: Category) => void;
  isDefault: boolean;
  t: TranslateFn;
}

function SortableCategoryItem({
  category,
  getCategoryIcon,
  onEdit,
  onDelete,
  onToggleHidden,
  isDefault,
  t,
}: SortableCategoryItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : category.is_hidden ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600 hover:border-gray-200 dark:hover:border-gray-500 transition-all group ${category.is_hidden ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="text-gray-400 dark:text-gray-500 cursor-grab hover:text-gray-600 dark:hover:text-gray-300 active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <span className="text-xl">{getCategoryIcon(category.icon)}</span>
      <span className="flex-1 text-gray-900 dark:text-white text-sm font-medium truncate">
        {category.name}
      </span>
      {category.is_hidden && (
        <EyeOff className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      )}
      <div
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: category.color }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(category)}>
            <Pencil className="w-4 h-4 mr-2" /> {t('categories.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onToggleHidden(category)}>
            {category.is_hidden ? (
              <>
                <Eye className="w-4 h-4 mr-2" /> {t('categories.show')}
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4 mr-2" /> {t('categories.hide')}
              </>
            )}
          </DropdownMenuItem>
          {!isDefault && (
            <DropdownMenuItem
              onClick={() => onDelete(category)}
              className="text-red-600 dark:text-red-400"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {t('categories.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Category Group Card Component
interface CategoryGroupCardProps {
  group: GroupWithCategories;
  getCategoryIcon: (icon: string | null) => string;
  onEditGroup: (group: CategoryGroup) => void;
  onDeleteGroup: (group: CategoryGroup) => void;
  onToggleGroupHidden: (group: CategoryGroup) => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  onToggleCategoryHidden: (category: Category) => void;
  onAddCategory: (groupId: string, type: 'income' | 'expense') => void;
  onReorderCategories: (categoryIds: string[], groupId: string) => void;
  t: TranslateFn;
}

function CategoryGroupCard({
  group,
  getCategoryIcon,
  onEditGroup,
  onDeleteGroup,
  onToggleGroupHidden,
  onEditCategory,
  onDeleteCategory,
  onToggleCategoryHidden,
  onAddCategory,
  onReorderCategories,
  t,
}: CategoryGroupCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Start drag after 5px movement for smoother experience
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = group.categories.findIndex((c) => c.id === active.id);
      const newIndex = group.categories.findIndex((c) => c.id === over.id);

      const newOrder = arrayMove(group.categories, oldIndex, newIndex);
      onReorderCategories(
        newOrder.map((c) => c.id),
        group.id
      );
    }
  };

  const categoryIds = useMemo(() => group.categories.map((c) => c.id), [group.categories]);

  return (
    <div
      className={`bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden ${group.is_hidden ? 'opacity-50' : ''}`}
    >
      {/* Group Header */}
      <div className="flex items-center gap-3 p-4 bg-gray-100/50 dark:bg-gray-700/50">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        <span className="flex-1 text-gray-900 dark:text-white font-semibold">{group.name}</span>
        {group.is_hidden && (
          <EyeOff className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        )}
        <span className="text-gray-500 dark:text-gray-400 text-sm px-2 py-0.5 bg-gray-200 dark:bg-gray-600 rounded-full">
          {group.categories.length}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEditGroup(group)}>
              <Pencil className="w-4 h-4 mr-2" /> {t('categories.editGroup')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onToggleGroupHidden(group)}>
              {group.is_hidden ? (
                <>
                  <Eye className="w-4 h-4 mr-2" /> {t('categories.showGroup')}
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 mr-2" /> {t('categories.hideGroup')}
                </>
              )}
            </DropdownMenuItem>
            {!group.is_default && (
              <DropdownMenuItem
                onClick={() => onDeleteGroup(group)}
                className="text-red-600 dark:text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" /> {t('categories.deleteGroup')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Categories List */}
      {isExpanded && (
        <div className="p-3 space-y-2">
          {group.categories.length === 0 ? (
            <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm">
              <Tag className="w-6 h-6 mx-auto mb-2 opacity-50" />
              {t('categories.noCategories')}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              measuring={{
                droppable: {
                  strategy: MeasuringStrategy.Always,
                },
              }}
            >
              <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                {group.categories.map((category) => (
                  <SortableCategoryItem
                    key={category.id}
                    category={category}
                    getCategoryIcon={getCategoryIcon}
                    onEdit={onEditCategory}
                    onDelete={onDeleteCategory}
                    onToggleHidden={onToggleCategoryHidden}
                    isDefault={category.is_default}
                    t={t}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          {/* Add Category Button */}
          <button
            onClick={() => onAddCategory(group.id, group.type)}
            className="w-full flex items-center justify-center gap-2 p-3 text-[#16c47f] hover:bg-[#16c47f]/10 rounded-xl transition-colors text-sm font-medium border-2 border-dashed border-gray-200 dark:border-gray-600 hover:border-[#16c47f]"
          >
            <Plus className="w-4 h-4" />
            {t('categories.createCategory')}
          </button>
        </div>
      )}
    </div>
  );
}

// Category Section Component
interface CategorySectionProps {
  title: string;
  type: 'income' | 'expense';
  groups: GroupWithCategories[];
  getCategoryIcon: (icon: string | null) => string;
  onCreateGroup: (type: 'income' | 'expense') => void;
  onEditGroup: (group: CategoryGroup) => void;
  onDeleteGroup: (group: CategoryGroup) => void;
  onToggleGroupHidden: (group: CategoryGroup) => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  onToggleCategoryHidden: (category: Category) => void;
  onAddCategory: (groupId: string, type: 'income' | 'expense') => void;
  onReorderCategories: (categoryIds: string[], groupId: string) => void;
  t: TranslateFn;
}

function CategorySection({
  title,
  type,
  groups,
  getCategoryIcon,
  onCreateGroup,
  onEditGroup,
  onDeleteGroup,
  onToggleGroupHidden,
  onEditCategory,
  onDeleteCategory,
  onToggleCategoryHidden,
  onAddCategory,
  onReorderCategories,
  t,
}: CategorySectionProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
        <button
          onClick={() => onCreateGroup(type)}
          className="text-[#16c47f] hover:text-[#12b070] text-sm font-medium flex items-center gap-1 hover:bg-[#16c47f]/10 px-3 py-1.5 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('categories.createGroup')}
        </button>
      </div>

      <div className="space-y-3">
        {groups.map((group) => (
          <CategoryGroupCard
            key={group.id}
            group={group}
            getCategoryIcon={getCategoryIcon}
            onEditGroup={onEditGroup}
            onDeleteGroup={onDeleteGroup}
            onToggleGroupHidden={onToggleGroupHidden}
            onEditCategory={onEditCategory}
            onDeleteCategory={onDeleteCategory}
            onToggleCategoryHidden={onToggleCategoryHidden}
            onAddCategory={onAddCategory}
            onReorderCategories={onReorderCategories}
            t={t}
          />
        ))}

        {groups.length === 0 && (
          <div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-100 dark:border-gray-700/50">
            <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-500 dark:text-gray-400 mb-2">{t('categories.noGroups')}</p>
            <Button
              onClick={() => onCreateGroup(type)}
              variant="outline"
              className="text-[#16c47f] border-[#16c47f] hover:bg-[#16c47f]/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('categories.createFirstGroup')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Main CategorySettings Component
export default function CategorySettings() {
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const {
    incomeGroups,
    expenseGroups,
    loading,
    error,
    createGroup,
    updateGroup,
    deleteGroup,
    toggleGroupHidden,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    toggleCategoryHidden,
    getCategoryIcon,
    loadData,
    seedDefaultData,
  } = useCategories();

  // Modal states
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);

  // Editing states
  const [editingGroup, setEditingGroup] = useState<CategoryGroup | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<CategoryGroup | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  // Form states
  const [groupForm, setGroupForm] = useState({ name: '', type: 'expense' as 'income' | 'expense' });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: '🏷️',
    color: '#16c47f',
    group_id: '',
    type: 'expense' as 'income' | 'expense',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Validation
  const validateGroupForm = () => {
    if (!groupForm.name.trim()) {
      setFormError(t('categories.validation.nameRequired'));
      return false;
    }
    if (groupForm.name.trim().length < 2) {
      setFormError(t('categories.validation.nameTooShort'));
      return false;
    }
    if (groupForm.name.trim().length > 50) {
      setFormError(t('categories.validation.nameTooLong'));
      return false;
    }
    setFormError(null);
    return true;
  };

  const validateCategoryForm = () => {
    if (!categoryForm.name.trim()) {
      setFormError(t('categories.validation.nameRequired'));
      return false;
    }
    if (categoryForm.name.trim().length < 2) {
      setFormError(t('categories.validation.nameTooShort'));
      return false;
    }
    if (categoryForm.name.trim().length > 50) {
      setFormError(t('categories.validation.nameTooLong'));
      return false;
    }
    setFormError(null);
    return true;
  };

  // Group handlers
  const handleCreateGroup = (type: 'income' | 'expense') => {
    setEditingGroup(null);
    setGroupForm({ name: '', type });
    setFormError(null);
    setGroupModalOpen(true);
  };

  const handleEditGroup = (group: CategoryGroup) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, type: group.type });
    setFormError(null);
    setGroupModalOpen(true);
  };

  const handleDeleteGroupClick = (group: CategoryGroup) => {
    setDeletingGroup(group);
    setDeleteGroupDialogOpen(true);
  };

  const handleGroupSubmit = async () => {
    if (!validateGroupForm()) return;

    setSubmitting(true);
    try {
      if (editingGroup) {
        const result = await updateGroup(editingGroup.id, { name: groupForm.name.trim() });
        if (result.success) {
          toast({
            title: t('categories.groupUpdated'),
            description: t('categories.groupUpdatedDesc'),
          });
          setGroupModalOpen(false);
        } else if (result.error === 'duplicate') {
          setFormError(t('categories.duplicateGroupError'));
        } else {
          throw new Error('Failed to update group');
        }
      } else {
        const result = await createGroup({ name: groupForm.name.trim(), type: groupForm.type });
        if (result.success) {
          toast({
            title: t('categories.groupCreated'),
            description: t('categories.groupCreatedDesc'),
          });
          setGroupModalOpen(false);
        } else if (result.error === 'duplicate') {
          setFormError(t('categories.duplicateGroupError'));
        } else {
          throw new Error('Failed to create group');
        }
      }
    } catch (err) {
      console.error(err);
      toast({
        title: t('common.error'),
        description: t('categories.groupError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGroupDelete = async () => {
    if (!deletingGroup) return;

    setSubmitting(true);
    try {
      const result = await deleteGroup(deletingGroup.id);
      if (result.success) {
        toast({
          title: t('categories.groupDeleted'),
          description: t('categories.groupDeletedDesc'),
        });
        setDeleteGroupDialogOpen(false);
        setDeletingGroup(null);
      } else {
        throw new Error(result.error as string);
      }
    } catch (err) {
      console.error(err);
      toast({
        title: t('common.error'),
        description: t('categories.groupDeleteError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Category handlers
  const handleAddCategory = (groupId: string, type: 'income' | 'expense') => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      icon: '🏷️',
      color: AVAILABLE_COLORS[Math.floor(Math.random() * AVAILABLE_COLORS.length)],
      group_id: groupId,
      type,
    });
    setFormError(null);
    setCategoryModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      icon: category.icon || '🏷️',
      color: category.color,
      group_id: category.group_id || '',
      type: category.type,
    });
    setFormError(null);
    setCategoryModalOpen(true);
  };

  const handleDeleteCategoryClick = (category: Category) => {
    setDeletingCategory(category);
    setDeleteCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async () => {
    if (!validateCategoryForm()) return;

    setSubmitting(true);
    try {
      if (editingCategory) {
        const result = await updateCategory(editingCategory.id, {
          name: categoryForm.name.trim(),
          icon: categoryForm.icon,
          color: categoryForm.color,
          group_id: categoryForm.group_id || null,
        });
        if (result.success) {
          toast({
            title: t('categories.categoryUpdated'),
            description: t('categories.categoryUpdatedDesc'),
          });
          setCategoryModalOpen(false);
        } else if (result.error === 'duplicate') {
          setFormError(t('categories.duplicateError'));
        } else {
          throw new Error('Failed to update category');
        }
      } else {
        const result = await createCategory({
          name: categoryForm.name.trim(),
          type: categoryForm.type,
          group_id: categoryForm.group_id || undefined,
          icon: categoryForm.icon,
          color: categoryForm.color,
        });
        if (result.success) {
          toast({
            title: t('categories.categoryCreated'),
            description: t('categories.categoryCreatedDesc'),
          });
          setCategoryModalOpen(false);
        } else if (result.error === 'duplicate') {
          setFormError(t('categories.duplicateError'));
        } else {
          throw new Error('Failed to create category');
        }
      }
    } catch (err) {
      console.error(err);
      toast({
        title: t('common.error'),
        description: t('categories.categoryError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategoryDelete = async () => {
    if (!deletingCategory) return;

    setSubmitting(true);
    try {
      const result = await deleteCategory(deletingCategory.id);
      if (result.success) {
        toast({
          title: t('categories.categoryDeleted'),
          description: t('categories.categoryDeletedDesc'),
        });
        setDeleteCategoryDialogOpen(false);
        setDeletingCategory(null);
      } else {
        throw new Error(result.error as string);
      }
    } catch (err) {
      console.error(err);
      toast({
        title: t('common.error'),
        description: t('categories.categoryDeleteError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReorderCategories = async (categoryIds: string[], groupId: string) => {
    const result = await reorderCategories(categoryIds, groupId);
    if (!result.success) {
      toast({
        title: t('common.error'),
        description: t('categories.reorderError'),
        variant: 'destructive',
      });
    }
  };

  const handleToggleCategoryHidden = async (category: Category) => {
    const result = await toggleCategoryHidden(category.id, category.is_hidden);
    if (result.success) {
      toast({
        title: category.is_hidden ? t('categories.categoryShown') : t('categories.categoryHidden'),
        description: category.is_hidden
          ? t('categories.categoryShownDesc')
          : t('categories.categoryHiddenDesc'),
      });
    } else {
      toast({
        title: t('common.error'),
        description: t('categories.toggleHiddenError'),
        variant: 'destructive',
      });
    }
  };

  const handleToggleGroupHidden = async (group: CategoryGroup) => {
    const result = await toggleGroupHidden(group.id, group.is_hidden);
    if (result.success) {
      toast({
        title: group.is_hidden ? t('categories.groupShown') : t('categories.groupHidden'),
        description: group.is_hidden
          ? t('categories.groupShownDesc')
          : t('categories.groupHiddenDesc'),
      });
    } else {
      toast({
        title: t('common.error'),
        description: t('categories.toggleHiddenError'),
        variant: 'destructive',
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 animate-spin text-[#16c47f] mb-4" />
        <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('categories.loadError')}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <Button onClick={loadData} className="bg-[#16c47f] hover:bg-[#12b070]">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  // Empty state (no categories at all)
  if (incomeGroups.length === 0 && expenseGroups.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50">
          <Tag className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('categories.emptyTitle')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-center max-w-md mb-6">
            {t('categories.emptyDesc')}
          </p>
          <Button onClick={seedDefaultData} className="bg-[#16c47f] hover:bg-[#12b070]">
            <Settings className="w-4 h-4 mr-2" />
            {t('categories.setupDefaults')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Income Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <CategorySection
          title={t('categories.income')}
          type="income"
          groups={incomeGroups}
          getCategoryIcon={getCategoryIcon}
          onCreateGroup={handleCreateGroup}
          onEditGroup={handleEditGroup}
          onDeleteGroup={handleDeleteGroupClick}
          onToggleGroupHidden={handleToggleGroupHidden}
          onEditCategory={handleEditCategory}
          onDeleteCategory={handleDeleteCategoryClick}
          onToggleCategoryHidden={handleToggleCategoryHidden}
          onAddCategory={handleAddCategory}
          onReorderCategories={handleReorderCategories}
          t={t}
        />
      </div>

      {/* Expenses Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <CategorySection
          title={t('categories.expenses')}
          type="expense"
          groups={expenseGroups}
          getCategoryIcon={getCategoryIcon}
          onCreateGroup={handleCreateGroup}
          onEditGroup={handleEditGroup}
          onDeleteGroup={handleDeleteGroupClick}
          onToggleGroupHidden={handleToggleGroupHidden}
          onEditCategory={handleEditCategory}
          onDeleteCategory={handleDeleteCategoryClick}
          onToggleCategoryHidden={handleToggleCategoryHidden}
          onAddCategory={handleAddCategory}
          onReorderCategories={handleReorderCategories}
          t={t}
        />
      </div>

      {/* Group Modal */}
      <Dialog
        open={groupModalOpen}
        onOpenChange={(open) => {
          if (!open) setFormError(null);
          setGroupModalOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? t('categories.editGroup') : t('categories.createGroup')}
            </DialogTitle>
            <DialogDescription>
              {editingGroup ? t('categories.editGroupDesc') : t('categories.createGroupDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="groupName">{t('categories.groupName')}</Label>
              <Input
                id="groupName"
                value={groupForm.name}
                onChange={(e) => {
                  setGroupForm({ ...groupForm, name: e.target.value });
                  setFormError(null);
                }}
                placeholder={t('categories.groupNamePlaceholder')}
                className={`h-11 ${formError ? 'border-red-500' : ''}`}
                onKeyDown={(e) => e.key === 'Enter' && handleGroupSubmit()}
                autoFocus
              />
              {formError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {formError}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleGroupSubmit}
              disabled={submitting}
              className="bg-[#16c47f] hover:bg-[#12b070]"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingGroup ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog
        open={categoryModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setFormError(null);
            setShowEmojiPicker(false);
          }
          setCategoryModalOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? t('categories.editCategory') : t('categories.createCategory')}
            </DialogTitle>
            <DialogDescription>
              {editingCategory
                ? t('categories.editCategoryDesc')
                : t('categories.createCategoryDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="categoryName">{t('categories.categoryName')}</Label>
              <Input
                id="categoryName"
                value={categoryForm.name}
                onChange={(e) => {
                  setCategoryForm({ ...categoryForm, name: e.target.value });
                  setFormError(null);
                }}
                placeholder={t('categories.categoryNamePlaceholder')}
                className={`h-11 ${formError ? 'border-red-500' : ''}`}
                onKeyDown={(e) => e.key === 'Enter' && handleCategorySubmit()}
                autoFocus
              />
              {formError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {formError}
                </p>
              )}
            </div>

            {/* Preview */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <span className="text-2xl">{getCategoryIcon(categoryForm.icon)}</span>
              <span className="flex-1 text-gray-900 dark:text-white font-medium">
                {categoryForm.name || t('categories.categoryNamePlaceholder')}
              </span>
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: categoryForm.color }}
              />
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <Label>{t('categories.icon')}</Label>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors w-full"
              >
                <span className="text-2xl">{getCategoryIcon(categoryForm.icon)}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('categories.chooseEmoji')}
                </span>
              </button>
              {showEmojiPicker && (
                <div className="mt-2">
                  <Picker
                    data={data}
                    onEmojiSelect={(emoji: { native: string }) => {
                      setCategoryForm({ ...categoryForm, icon: emoji.native });
                      setShowEmojiPicker(false);
                    }}
                    locale={language === 'fr' ? 'fr' : 'en'}
                    theme="auto"
                    previewPosition="none"
                    skinTonePosition="search"
                    maxFrequentRows={2}
                  />
                </div>
              )}
            </div>

            {/* Color Selection */}
            <div className="space-y-2">
              <Label>{t('categories.color')}</Label>
              <div className="flex gap-2 flex-wrap">
                {AVAILABLE_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setCategoryForm({ ...categoryForm, color })}
                    className={`w-8 h-8 rounded-full transition-all ${
                      categoryForm.color === color
                        ? 'ring-2 ring-offset-2 dark:ring-offset-gray-800 scale-110'
                        : 'hover:scale-110'
                    }`}
                    style={{
                      backgroundColor: color,
                      boxShadow: categoryForm.color === color ? `0 0 0 2px ${color}` : undefined,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCategorySubmit}
              disabled={submitting}
              className="bg-[#16c47f] hover:bg-[#12b070]"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCategory ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Group Confirmation */}
      <AlertDialog open={deleteGroupDialogOpen} onOpenChange={setDeleteGroupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('categories.deleteGroupConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('categories.deleteGroupWarning').replace('{name}', deletingGroup?.name || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleGroupDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('categories.deleteCategoryConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('categories.deleteCategoryWarning').replace(
                '{name}',
                deletingCategory?.name || ''
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCategoryDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
